import {
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {TOOLTIP_BASE_OPTIONS} from '../shared/helpers/tooltip-defaults';
import {GraphLayouts, GraphMap, LayoutType} from '../api/interfaces/graph-node.interface';
import {MapService} from '../api/services/map.service';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {debounceTime, distinctUntilChanged, filter, map, skip, switchMap, tap} from 'rxjs';
import {UserService} from '../api/services/user.service';
import {DefaultValues, MODULE_NAMES, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {SidebarService} from '../sidebar-form/sidebar.service';
import {MetaDataControlComponent} from '../shared/forms/key-value-table/meta-data-control.component';
import {isEqual, orderBy, sortBy} from 'lodash';
import {MetadataService} from '../api/services/metadata.service';
import {MetaDataViewerComponent} from '../shared/forms/meta-data-viewer/meta-data-viewer.componet';
import {
  TitleDescriptionInputComponent
} from '../shared/forms/title-description-input/title-description-input.component';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {CommentsComponent} from '../shared/components/comments/comments.component';
import {AppWideStateService} from '../services/app-state/app-wide-state.service';
import {OpenaiService} from '../api/services/openai.service';
import {GenerationStateService} from '../services/generation-state.service';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {TranslatePipe} from '@ngx-translate/core';
import {ColorInputComponent} from '../shared/forms/color-input/color-input.component';
import {AiStateService} from '../services/ai-state.service';
import {MatDialog} from '@angular/material/dialog';
import {ImageReachabilityDirective} from '../shared/directives/url-validator/image-url-validator.directive';
import {AudioReachabilityDirective} from '../shared/directives/url-validator/audio-url-validator.directive';
import {TextInputComponent} from '../shared/forms/text-input/text-input.component';
import {SliderInputComponent} from '../shared/forms/slider-input/slider-input.component';
import {HasPackageDirective} from '../packages/has-package.directive';
import {HasMapPermissionDirective} from '../packages/has-map-permission.directive';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {StyleGroupExpansionService} from '../shared/style-groups/style-group-expansion.service';
import {coreStyleGroupId, styleGroupToggleTooltip} from '../shared/style-groups/style-group.util';
import {StyleRowComponent} from '../shared/style-groups/style-row.component';
import {
  AIGenerationBubbleConfig,
  AiGenerationBubbleComponent
} from '../shared/components/ai-generation-bubble/ai-generation-bubble.component';
import {
  ImageWarningPreferenceService
} from '../shared/components/ai-generation-bubble/image-warning-preference.service';
import {ConnectedOverlayPositionChange, OverlayModule} from '@angular/cdk/overlay';
import {
  AI_BUBBLE_POSITIONS_END,
  isBubbleAbove
} from '../shared/components/ai-generation-bubble/ai-bubble-overlay';
import {TourMatMenuModule} from 'ngx-ui-tour-md-menu';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {MapRoleService} from '../acl/map-role.service';
import {PackageService} from '../api/services/package.service';
import {MapAudioIntegrationService} from '../services/map-audio-integration.service';
import {TopToolbarStateService} from '../nav/top-toolbar-state.service';
import {UndoRedoManagerService} from '../services/undo-redo/undo-redo-manager.service';
import {SmapStyleCommand} from '../services/undo-redo/commands/smap-style.command';
import {SmapTextChangeCommand} from '../services/undo-redo/commands/smap-text-change.command';
import {SmapPropertyCommand} from '../services/undo-redo/commands/smap-property.command';
import {undoRedoJustHappened} from '../shared/helpers/signal.helpers';
import {TrialStateService} from '../services/trial-state.service';

@Component({
  selector: 'maporium-map-info-section',
  template: `
    @if (!hasNoMap) {
      <div class="main-content maporium-map-info-section">
        <maporium-title-description [fields]="nameDescriptionFields"
                                    (fieldsChange)="onNameDescriptionChange($event)"
                                    [allowMultiLine]="false"
                                    [entityId]="selectedMap?.id"
                                    [isMap]="true"
                                    [isNew]="resetForm"
                                    [isEditing]="isEditable && !readModeAppState"></maporium-title-description>

        @if (metadata.length && isInIframe()) {
          <mat-divider class="mb-2 mt-2"></mat-divider>
        }
        <div class="mt-3">
          @if (!isInIframe()) {
            <cdk-accordion [multi]="true" class="accordion">
              <maporium-accordion-item [title]="'MAP_INFO.PROPERTIES' | translate"
                                       [iconClass]="'icon-smap-object-property'"
                                       tourAnchor="properties-anchor"
                                       [sections]="metadata?.length || 0">
                <ng-container
                  *ngIf="!isEditMode || readonly || readModeAppState || isDefaulter || (!isMapOwner && !hasEditPermission); else mapName">
                  <ng-container *ngIf="metadata">
                    <maporium-meta-viewer [metadata]="metadata"></maporium-meta-viewer>
                  </ng-container>
                </ng-container>
              </maporium-accordion-item>
              @if ((selectedMap?.id && !readModeAppState) || selectedMap?.hasGuestComments) {
                <maporium-accordion-item [title]="'MAP_INFO.COMMENTS' | translate" *hasMapPermission="'comment'"
                                         [iconClass]="'icon-smap-object-comment'"
                                         [sections]="commentsCount">
                  <maporium-comments [entityId]="selectedMap!.id"
                                     (commentCount)="commentsCount = $event"
                                     [entityType]="'map'"
                                     [isEditing]="!isDefaulter"></maporium-comments>
                </maporium-accordion-item>
              }
              @if (isEditMode && !readModeAppState && !readonly) {
                <maporium-accordion-item [title]="'MAP_INFO.STYLES.TITLE' | translate"
                                         [iconClass]="'icon-smap-object-style'"
                                         [sections]="sectionsCount"
                                         *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.developer, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher]">
                  <form [formGroup]="styleForm">
                    <div id="map-styles">
                      <!-- APPEARANCE (core, no dependents -> no toggle) -->
                      <maporium-style-row [divider]="false">
                        <app-select-input
                          formControlName="appearance"
                          [options]="smapAppearanceOptions"
                          [label]="'contrast'"
                          [isIcon]="true"
                          [placeholder]="'MAP_INFO.STYLES.APPEARANCE_PLACEHOLDER' | translate">
                        </app-select-input>
                      </maporium-style-row>

                      <!-- COLOR (core) -> color intensity (dependent) -->
                      <maporium-style-row [hasToggle]="true"
                                          [expanded]="isExpanded('color')"
                                          [tooltip]="toggleTooltip('color')"
                                          (toggleClick)="toggle('color')">
                        <app-color-input [placeholder]="'MAP_INFO.STYLES.COLOR_FILLING' | translate"
                                         [icon]="'palette'"
                                         [value]="selectedMap?.styles?.color ?? '#ffffff'"
                                         formControlName="color"></app-color-input>
                      </maporium-style-row>
                      @if (isExpanded('color')) {
                        <maporium-style-row [divider]="false">
                          <app-slider-input [label]="'colors'"
                                            [value]="selectedMap?.styles?.colorIntensity ?? 0"
                                            [isIcon]="true"
                                            [discrete]="true"
                                            [placeholder]="'MAP_INFO.STYLES.INTENSITY' | translate"
                                            formControlName="colorIntensity"
                                            [step]="0.1"
                                            [max]="1"
                                            [min]="0">
                          </app-slider-input>
                        </maporium-style-row>
                      }

                      <!-- BACKGROUND IMAGE (core) -> background image size,
                           background image opacity (dependents) -->
                      <maporium-style-row [hasToggle]="true"
                                          [expanded]="isExpanded('backgroundImage')"
                                          [tooltip]="toggleTooltip('background image')"
                                          (toggleClick)="toggle('backgroundImage')">
                        <app-text-input formControlName="imageUrl"
                                        [tooltip]="'MAP_INFO.STYLES.SET_BACKGROUND_IMAGE_URL_TOOLTIP' | translate"
                                        [placeholder]="'MAP_INFO.STYLES.IMAGE_URL_PLACEHOLDER' | translate"
                                        appImageReachability
                                        [autoFocusAndSelect]="true"
                                        [label]="'image'"
                                        [isIcon]="true">
                          @if (packageService.isOnTestPlan() && aiStateService.isAiVisible()) {
                            <span class="find-image-anchor">
                              <span class="find-image-slot"
                                    cdkOverlayOrigin
                                    #findSmapImageTrigger="cdkOverlayOrigin"
                                    data-generate-button
                                    [class.is-finding]="findingSmapImage"
                                    (click)="onFindSmapImageClick()"
                                    [matTooltipPosition]="'left'"
                                    [matTooltip]="'MAP_INFO.STYLES.FIND_BACKGROUND_IMAGE_TOOLTIP' | translate">
                                <span class="icon-smap-action-generate"></span>
                              </span>
                              <ng-template cdkConnectedOverlay
                                           [cdkConnectedOverlayOrigin]="findSmapImageTrigger"
                                           [cdkConnectedOverlayOpen]="showImageWarning"
                                           [cdkConnectedOverlayPositions]="imageWarningPositions"
                                           [cdkConnectedOverlayViewportMargin]="8"
                                           (positionChange)="onImageWarningPositionChange($event)"
                                           (overlayKeydown)="onImageWarningKeydown($event)"
                                           (detach)="showImageWarning = false">
                                <app-ai-generation-bubble
                                  class="arrow-end"
                                  [class.arrow-down]="imageWarningAbove"
                                  [config]="imageWarningConfig"
                                  (generate)="onImageWarningGenerate()"
                                  (close)="showImageWarning = false">
                                </app-ai-generation-bubble>
                              </ng-template>
                            </span>
                          }
                        </app-text-input>
                      </maporium-style-row>
                      @if (isExpanded('backgroundImage')) {
                        <maporium-style-row [divider]="false">
                          <app-slider-input [label]="'picture_in_picture_center'"
                                            [value]="selectedMap?.styles?.backgroundImageSize ?? 0"
                                            [isIcon]="true"
                                            [discrete]="true"
                                            [showTickMarks]="false"
                                            [placeholder]="'MAP_INFO.STYLES.BACKGROUND_IMAGE_SIZE' | translate"
                                            formControlName="backgroundImageSize"
                                            [step]="0.01"
                                            [max]="1"
                                            [min]="-1">
                          </app-slider-input>
                        </maporium-style-row>
                        <maporium-style-row [divider]="false">
                          <app-slider-input [label]="'opacity'"
                                            [value]="selectedMap?.styles?.opacity ?? 1"
                                            [isIcon]="true"
                                            [discrete]="true"
                                            [placeholder]="'MAP_INFO.STYLES.BACKGROUND_IMAGE_OPACITY' | translate"
                                            formControlName="imageOpacity"
                                            [step]="0.1"
                                            [max]="1"
                                            [min]="0">
                          </app-slider-input>
                        </maporium-style-row>
                      }

                      <!-- AMBIENT SOUND (core) -> sound volume (dependent) -->
                      <maporium-style-row [hasToggle]="true"
                                          [expanded]="isExpanded('ambientSound')"
                                          [tooltip]="toggleTooltip('ambient sound')"
                                          (toggleClick)="toggle('ambientSound')">
                        <app-text-input formControlName="backgroundSoundUrl"
                                        [placeholder]="'MAP_INFO.STYLES.SOUND_URL_PLACEHOLDER' | translate"
                                        [tooltip]="'MAP_INFO.STYLES.SET_SOUND_URL_TOOLTIP' | translate"
                                        appAudioReachability
                                        [autoFocusAndSelect]="true"
                                        [label]="'graphic_eq'"
                                        [isIcon]="true"></app-text-input>
                      </maporium-style-row>
                      @if (isExpanded('ambientSound')) {
                        <maporium-style-row [divider]="false">
                          <app-slider-input [label]="'volume_up'"
                                            [value]="selectedMap?.styles?.backgroundSoundVolume ?? 0.5"
                                            [isIcon]="true"
                                            [discrete]="true"
                                            [placeholder]="'MAP_INFO.STYLES.SOUND_VOLUME' | translate"
                                            formControlName="backgroundSoundVolume"
                                            [step]="0.1"
                                            [max]="1"
                                            [min]="0">
                          </app-slider-input>
                        </maporium-style-row>
                      }

                      <!-- Not part of the smap "styles" hierarchy (a layout algorithm
                           choice, not a visual style) — kept as its own ungrouped,
                           non-collapsible row. -->
                      <maporium-style-row *appHasPackage="[PACKAGE_NAMES.developer]">
                        <app-select-input [formControl]="layoutFormControl"
                                          [label]="'graph_3'"
                                          [placeholder]="'MAP_INFO.STYLES.AUTOMATED_LAYOUT_PLACEHOLDER' | translate"
                                          [isIcon]="true"
                                          [options]="layoutOptions">
                        </app-select-input>
                      </maporium-style-row>
                    </div>
                  </form>

                </maporium-accordion-item>
              }
            </cdk-accordion>
          } @else {
            <div tourAnchor="properties-anchor" class="smaplet-properties-tour-anchor">
              <maporium-meta-viewer [metadata]="metadata"></maporium-meta-viewer>
            </div>

            @if (selectedMap?.hasGuestComments) {
              <maporium-accordion-item [title]="'MAP_INFO.COMMENTS' | translate"
                                       [iconClass]="'icon-smap-object-comment'"

                                       [sections]="commentsCount">
                <maporium-comments [entityId]="selectedMap!.id"
                                   (commentCount)="commentsCount = $event"
                                   [entityType]="'map'"
                                   [isEditing]="true"></maporium-comments>
              </maporium-accordion-item>
            }
          }
        </div>


        <ng-template #mapName>
          <form [formGroup]="mapForm" autocomplete="off" class="mb-3">
            <maporium-meta-data-control formControlName="metadata"
                                        [sorting]="true"
                                        [forMap]="true"
                                        (extendMeta)="onMetaExtend()"
                                        (addMeta)="onAddMeta($event)"></maporium-meta-data-control>
          </form>
        </ng-template>
      </div>
    }
  `,
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MetaDataControlComponent,
    MetaDataViewerComponent,
    TitleDescriptionInputComponent,
    MaporiumAccordionItemComponent,
    CdkAccordion,
    CommentsComponent,
    TranslatePipe,
    ColorInputComponent,
    ImageReachabilityDirective,
    AudioReachabilityDirective,
    TextInputComponent,
    SliderInputComponent,
    HasPackageDirective,
    HasMapPermissionDirective,
    SelectInputComponent,
    StyleRowComponent,
    TourMatMenuModule,
    AiGenerationBubbleComponent,
    OverlayModule
  ],
  styleUrls: ['./map-info-section.component.scss'],
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
        ...TOOLTIP_BASE_OPTIONS,
        position: 'left'
      }
    }
  ]
})

export class MapInfoSectionComponent implements OnInit {
  MODULE_NAMES = MODULE_NAMES;
  public isEditMode = false;
  public mapForm: FormGroup;
  public selectedMap: GraphMap | undefined;
  public nameDescriptionFields: {
    title: string;
    description?: string;
  } = { title: '', description: '' };
  public isValid = true;
  readModeAppState = false;
  public resetForm = false;

  @ViewChild('mapNameInput') mapNameInput!: ElementRef;
  @Input() readonly = false;
  @Input() hasNoMap = false;
  @Input() isLinkingInProgress = false;
  @Output() mapMetaChanged = new EventEmitter<GraphMap>;

  private newMetaAdded = false;
  private debounceTime = 400;
  private temporalMap: GraphMap | undefined;
  private isReceivingLocalUpdate = false;
  private changingMapTitle = false;

  // Track "before" values for undo
  private beforeName: string | undefined;
  private beforeDescription: string | undefined;
  private beforeStyles: Record<string, unknown> | undefined;
  private beforeMetadata: unknown[] | undefined;
  private nameFocusSessionId: string | undefined;
  private descriptionFocusSessionId: string | undefined;
  // MAP-1547: last map id whose style fields were patched into styleForm, used to
  // tell a genuine map switch (must re-patch) apart from a self-inflicted store
  // update mid-edit (must NOT re-patch, or it clobbers the field being typed in).
  private lastNamedMapId: string | undefined;
  private lastStyledMapId: string | undefined;

  commentsCount = 0;

  // MAP-1533 Part C: in-flight guard for the smap "find image" button.
  public findingSmapImage = false;

  // MAP-1583: experimental-image warning bubble, shown as a confirmation gate
  // before the lookup runs (unless the user opted out of it).
  public showImageWarning = false;
  public imageWarningAbove = false;
  public readonly imageWarningConfig: AIGenerationBubbleConfig = {
    type: 'image-warning',
    trialLocked: true,
    imageWarningTarget: 'smap',
  };
  public readonly imageWarningPositions = AI_BUBBLE_POSITIONS_END;

  styleForm: FormGroup;
  protected readonly DefaultValues = DefaultValues;
  sectionsCount = 0;

  layoutOptions = GraphLayouts;

  smapAppearanceOptions = [
    { key: 'default', value: 'default', icon: 'settings', isSeparatorAfter: true },
    { key: 'light', value: 'light', icon: 'light_mode' },
    { key: 'dark', value: 'dark', icon: 'dark_mode' }
  ];

  layoutFormControl = new FormControl('preset');

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }

  get isEditable() {
    return this.isEditMode && (this.isMapOwner || this.hasEditPermission) && !this.isReadOnly;
  }

  get isMapOwner() {
    return this.selectedMap?.owner?.id === this.userService.getCurrentUserFromStorage()?.id;
  }

  get isReadOnly() {
    // Only consider it readonly if the map is explicitly readonly AND the user doesn't have edit permissions
    return this.selectedMap?.isReadonly && !this.hasEditPermission && !this.isMapOwner;
  }

  get hasEditPermission() {
    return this.mapRoleService.hasCurrentUserPermission('edit') || this.mapRoleService.hasCurrentUserPermission('manage');
  }

  get metadata() {
    if (!this.selectedMap) return [];
    return orderBy(this.selectedMap?.metadata, 'order', 'asc');
  }

  constructor(private mapService: MapService,
              private fb: FormBuilder,
              private el: ElementRef,
              private cdr: ChangeDetectorRef,
              private toolbarStateService: TopToolbarStateService,
              private metadataService: MetadataService,
              private openaiService: OpenaiService,
              private cyService: CytoscapeService,
              private sidebarService: SidebarService,
              private appWideStateService: AppWideStateService,
              private userService: UserService,
              private mapRoleService: MapRoleService,
              private mapAudioIntegrationService: MapAudioIntegrationService,
              protected aiStateService: AiStateService,
              private undoRedoManager: UndoRedoManagerService,
              private dialog: MatDialog,
              protected packageService: PackageService,
              private styleGroupExpansion: StyleGroupExpansionService,
              private imageWarningPreference: ImageWarningPreferenceService,
              private trialStateService: TrialStateService,
              private generationStateService: GenerationStateService) {
    this.mapForm = this.fb.group({
      metadata: new FormControl([])
    });

    this.styleForm = this.fb.group({
      appearance: new FormControl(DefaultValues.appearance.smap),
      color: new FormControl(''),
      colorIntensity: new FormControl(''),
      imageUrl: new FormControl(''),
      imageOpacity: new FormControl(''),
      backgroundImageSize: new FormControl(0),
      backgroundSoundUrl: new FormControl(''),
      backgroundSoundVolume: new FormControl('')
    });

    effect(() => {
      this.readModeAppState = this.appWideStateService.readMode();
    });

    effect(() => {
      this.isEditMode = this.sidebarService.getEditModeSignal();
      this.checkMapIsEditable();
    }, { allowSignalWrites: true });

    effect(() => {
      const map = this.mapService.getSelectedMapSignal();

      if (map) {
        this.resetForm = true;
        //@ts-ignore
        this.selectedMap = map?.name ? map : map.map as GraphMap;

        // MAP-1616: `selectedMap` moves to the new smap unconditionally, but the
        // displayed title used to be refreshed only inside the metadata guard
        // below. Coming back to a smap from a cross-map assistant notification,
        // the pushed map can arrive without metadata, so the sidebar kept the
        // PREVIOUS smap's name while the toolbar showed the right one. The
        // corrective effect further down could not save it either: it only fires
        // when the toolbar name differs from `selectedMap.name`, and by then they
        // already agree. Re-read the name on every real switch instead.
        if (this.selectedMap?.id !== this.lastNamedMapId) {
          this.lastNamedMapId = this.selectedMap?.id;
          this.nameDescriptionFields = {
            title: this.selectedMap?.name || '',
            description: this.selectedMap?.description || ''
          };
        }

        if (this.selectedMap?.metadata !== undefined) {
          this.nameDescriptionFields = {
            title: this.selectedMap?.name || '',
            description: this.selectedMap?.description || ''
          };

          if (!isEqual(this.selectedMap?.metadata, this.mapForm.value.metadata?.pairs) && !this.newMetaAdded) {
            this.mapForm.setValue({
              metadata: this.selectedMap?.metadata ? orderBy(this.selectedMap?.metadata, 'order', ['asc']) : []
            }, {emitEvent: false});
          }
          // MAP-1547: saving a style field pushes the map back into the store,
          // which re-fires this effect with the SAME map. Re-running setValue then
          // resets the imageUrl/backgroundSoundUrl inputs mid-typing and eats the
          // characters typed since the last debounced save (the same class of bug
          // MAP-1543 fixed for node/link via an "actively editing" guard). Only
          // re-patch styleForm on a real map switch, or when the user is not
          // currently editing a style field.
          const sameMap = this.selectedMap?.id === this.lastStyledMapId;
          const editingStyleField = this.el?.nativeElement?.contains(document.activeElement);
          if (!sameMap || !editingStyleField || undoRedoJustHappened()) {
            this.styleForm.setValue({
              appearance: this.selectedMap?.styles?.appearance ?? DefaultValues.appearance.smap,
              color: this.selectedMap?.styles?.color || '',
              colorIntensity: this.selectedMap?.styles?.colorIntensity ?? '0',
              imageUrl: this.selectedMap?.styles?.backgroundImageUrl || '',
              imageOpacity: this.selectedMap?.styles?.opacity ?? '0.5',
              backgroundImageSize: this.selectedMap?.styles?.backgroundImageSize ?? 0,
              backgroundSoundUrl: this.selectedMap?.styles?.backgroundSoundUrl || '',
              backgroundSoundVolume: this.selectedMap?.styles?.backgroundSoundVolume ?? '0.5'
            }, {emitEvent: false});
          }
          this.lastStyledMapId = this.selectedMap?.id;

          // Set the layout form control to the saved layout (default to 'preset' if not set)
          this.layoutFormControl.setValue(this.selectedMap?.automaticLayout || 'preset', {emitEvent: false});
        }

        this.checkMapIsEditable();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const mapName = this.toolbarStateService.mapName$();
      if (this.selectedMap?.name !== mapName) {
        if (!this.changingMapTitle) {
          this.nameDescriptionFields = {
            title: mapName || '',
            description: this.selectedMap?.description || ''
          };
          this.changingMapTitle = false;
        }
      }
    });
  }


  onAddMeta(pair: FormGroup<{ id: FormControl, value: FormControl, key: FormControl }>) {
    if (!this.selectedMap) return;
    this.newMetaAdded = true;
    this.metadataService.createOneForMap(this.selectedMap.id, pair.value as any)
      .subscribe((meta) => {
        pair.patchValue(meta);
        this.newMetaAdded = false;
      });
  }

  updateSectionCount() {
    setTimeout(() => {
      // MAP-990: "light"/"advanced" style sub-containers are gone — a single
      // #map-styles wraps the four core style rows (appearance, color,
      // background image, ambient sound) and whichever dependents are
      // currently expanded (see isExpanded()).
      this.sectionsCount = document.getElementById('map-styles')?.querySelectorAll(':scope > *').length ?? 0;
    }, 100);
  }

  isExpanded(coreStyleId: string): boolean {
    return this.styleGroupExpansion.isExpanded(`smap.${coreStyleId}`);
  }

  toggle(coreStyleId: string): void {
    this.styleGroupExpansion.toggle(`smap.${coreStyleId}`);
  }

  toggleTooltip(coreStyleName: string): string {
    return styleGroupToggleTooltip(coreStyleName, this.isExpanded(coreStyleGroupId(coreStyleName)));
  }

  onNameDescriptionChange(fields: { title: string, description?: string, valid?: boolean }) {
    if (!this.isEditable) return;
    if (!fields.valid) {
      this.isValid = false;
      return;
    }
    // Skip processing during undo/redo to prevent sending stale data
    if (undoRedoJustHappened()) {
      return;
    }

    this.nameDescriptionFields = fields;
    const nameChanged = this.selectedMap?.name !== fields.title;
    const descriptionChanged = this.selectedMap?.description !== fields.description;
    const hasChanged = nameChanged || descriptionChanged;
    if (!hasChanged) return;
    if (!this.selectedMap) return;

    // Capture "before" values for undo (only if not already captured)
    const mapId = this.selectedMap.id;
    if (nameChanged && this.beforeName === undefined) {
      this.beforeName = this.selectedMap.name;
      this.nameFocusSessionId = `smap-name-${mapId}-${Date.now()}`;
    }
    if (descriptionChanged && this.beforeDescription === undefined) {
      this.beforeDescription = this.selectedMap.description || '';
      this.descriptionFocusSessionId = `smap-description-${mapId}-${Date.now()}`;
    }

    // Update local state immediately (optimistic update)
    this.selectedMap.name = fields.title;
    this.selectedMap.description = fields.description;

    this.changingMapTitle = true;
    this.toolbarStateService.setMapName(fields.title);

    // The server just stores what we send for name/description, no server-side modifications
    this.mapService.update(this.selectedMap).subscribe({
      complete: () => {
        // Create undo commands for name/description changes
        if (nameChanged && this.beforeName !== undefined && mapId) {
          const textCommand = new SmapTextChangeCommand(
            this.mapService,
            {
              mapId,
              field: 'name',
              before: this.beforeName,
              after: fields.title
            },
            this.nameFocusSessionId!
          );
          this.undoRedoManager.pushCommand(textCommand);
          this.beforeName = undefined; // Reset for next change
        }

        if (descriptionChanged && this.beforeDescription !== undefined && mapId) {
          const textCommand = new SmapTextChangeCommand(
            this.mapService,
            {
              mapId,
              field: 'description',
              before: this.beforeDescription,
              after: fields.description || ''
            },
            this.descriptionFocusSessionId!
          );
          this.undoRedoManager.pushCommand(textCommand);
          this.beforeDescription = undefined; // Reset for next change
        }

        // Clear the sidebar updating flag after a brief delay
        setTimeout(() => {
          this.mapService.sidebarUpdatingSubject.next(false);
        }, 200);
      }
    });
  }

  private checkMapIsEditable() {
    if (!this.selectedMap) return;
    if (this.isEditable) {
      this.mapForm.enable({emitEvent: false});
      this.mapForm.updateValueAndValidity({emitEvent: false});
    } else {
      this.mapForm.disable({emitEvent: false});
    }
  }

  onMetaExtend() {
    // Only proceed if AI features are enabled (button should be disabled otherwise)
    if (!this.aiStateService.areAIFeaturesEnabled()) {
      return;
    }

    const signalData = this.openaiService.extendedMetadataSignal();
    if (signalData && signalData.source === 'map') {
      this.openaiService.extendedMetadataSignal.set(null);
      this.applyGeneratedMetadata(signalData.result as any);
      return;
    }

    // Direct generation (no bubble) - make API call
    if (!this.selectedMap) return;
    const dto = {
      title: this.selectedMap.name || '',
      description: this.selectedMap.description || '',
      metadata: this.selectedMap.metadata || [],
    };
    // Drives the generate icon animation (MAP-1402).
    const taskId = `map-info:${this.selectedMap.id}:${Date.now()}`;
    this.generationStateService.startPropertyGeneration(taskId);
    this.openaiService.extendMetadata(dto, this.selectedMap.id, undefined, 'map').subscribe({
      next: (res) => {
        this.generationStateService.endPropertyGeneration(taskId);
        if (!res) return;
        this.openaiService.extendedMetadataSignal.set(null);
        this.applyGeneratedMetadata(res);
      },
      error: () => {
        this.generationStateService.endPropertyGeneration(taskId);
      },
    });
  }

  private applyGeneratedMetadata(result: { metadata: any; description: string }) {
    if (!this.selectedMap) return;
    this.selectedMap.metadata = result.metadata;
    this.selectedMap.description = result.description;
    this.mapForm.setValue({
      metadata: result.metadata
    });
    this.mapService.update(this.selectedMap).subscribe();
  }

  onFindSmapImageClick() {
    if (this.findingSmapImage) return;
    // MAP-1583 (review round 3): re-clicking the trigger closes the bubble.
    // The bubble's own outside-click guard skips [data-generate-button], so
    // without this the second click was a no-op.
    if (this.showImageWarning) {
      this.showImageWarning = false;
      return;
    }
    if (!this.smapImageSearchInput) return;

    this.trialStateService.refresh();
    // The trial bubble has no "do not show this again" box, so a saved
    // dismissal must not skip past it into a lookup the trial cannot run.
    if (!this.trialStateService.isOnTrial && this.imageWarningPreference.isDismissed()) {
      this.runFindSmapImage();
      return;
    }
    this.showImageWarning = true;
  }

  // The bubble persists the "do not show this again" choice itself, the moment
  // it is ticked, so every close path honours it (MAP-1583 review round 3).
  onImageWarningGenerate() {
    if (this.trialStateService.isOnTrial) return;
    this.runFindSmapImage();
  }

  onImageWarningKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.showImageWarning = false;
    }
  }

  onImageWarningPositionChange(change: ConnectedOverlayPositionChange) {
    this.imageWarningAbove = isBubbleAbove(change);
  }

  private get smapImageSearchInput(): { name: string; description: string } | null {
    const name = this.nameDescriptionFields?.title || this.selectedMap?.name || '';
    const description = this.nameDescriptionFields?.description || this.selectedMap?.description || '';
    return name || description ? {name, description} : null;
  }

  private runFindSmapImage() {
    const input = this.smapImageSearchInput;
    if (!input) return;

    this.findingSmapImage = true;
    this.openaiService.findImage(input.name, input.description, 'smap').subscribe({
      next: (res) => {
        this.findingSmapImage = false;
        this.showImageWarning = false;
        if (res?.imageUrl) {
          this.styleForm.get('imageUrl')?.setValue(res.imageUrl);
        }
      },
      error: () => {
        this.findingSmapImage = false;
        this.showImageWarning = false;
      }
    });
  }

  protected readonly isInIframe = isInIframe;

  ngOnInit() {
    const currentSelected = this.mapService.getCurrentSelectedMapFromStore();
    this.nameDescriptionFields = {
      title: currentSelected?.name || ' ',
      description: currentSelected?.description || ' '
    };
    this.mapForm.patchValue({
      metadata: currentSelected?.metadata ? orderBy(currentSelected.metadata, 'order', ['asc']) : []
    }, {emitEvent: false});
    this.styleForm.patchValue({
      appearance: currentSelected?.styles?.appearance ?? DefaultValues.appearance.smap,
      color: currentSelected?.styles?.color || '',
      colorIntensity: currentSelected?.styles?.colorIntensity ?? '0',
      imageUrl: currentSelected?.styles?.backgroundImageUrl || '',
      imageOpacity: currentSelected?.styles?.opacity ?? '0.5',
      backgroundImageSize: currentSelected?.styles?.backgroundImageSize ?? 0,
      backgroundSoundUrl: currentSelected?.styles?.backgroundSoundUrl || '',
      backgroundSoundVolume: currentSelected?.styles?.backgroundSoundVolume ?? '0.5'
    }, {emitEvent: false});

    // Set the layout form control to the saved layout (default to 'preset' if not set)
    this.layoutFormControl.setValue(currentSelected?.automaticLayout || 'preset', {emitEvent: false});

    this.mapForm.valueChanges
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        filter(() => !this.newMetaAdded && !this.isReceivingLocalUpdate)
      )
      .subscribe((value) => {
        if (this.isLinkingInProgress) return;
        if (!this.selectedMap) return;
        if (!this.mapForm.valid && (this.mapForm.errors !== null && this.mapForm.enabled)) return;
        if (this.newMetaAdded) return;
        if (this.isReceivingLocalUpdate) return; // Additional guard

        const hastMetadataChanged = !isEqual(sortBy(this.selectedMap.metadata, 'id'), sortBy(value.metadata.pairs, 'id')) && value.metadata?.pairs !== undefined;
        const hasChanged = this.selectedMap.name !== this.nameDescriptionFields.title || this.selectedMap.description !== this.nameDescriptionFields.description;

        // Capture "before" metadata for undo (only if not already captured)
        if (hastMetadataChanged && this.beforeMetadata === undefined) {
          this.beforeMetadata = this.selectedMap.metadata ? [...this.selectedMap.metadata] : [];
        }

        if (hastMetadataChanged) {
          this.selectedMap.metadata = value.metadata?.pairs?.filter((m: any) => (m.key && m.key.trim() !== '') || (m.value && m.value.trim() !== ''));
          this.cdr.detectChanges();
        }

        if (this.isEditable && (hasChanged || hastMetadataChanged)) {
          // mapForm should only handle name/description/metadata changes
          // Styles are handled separately by styleForm.valueChanges
          this.mapService.setMapNameSignal(this.selectedMap.name);
          const mapId = this.selectedMap.id;
          const capturedBeforeMetadata = this.beforeMetadata;
          const afterMetadata = value.metadata?.pairs ? [...value.metadata.pairs] : [];

          this.mapService.update(this.selectedMap)
            .subscribe((map) => {
              // Create SmapPropertyCommand for metadata changes
              if (hastMetadataChanged && capturedBeforeMetadata !== undefined && mapId) {
                // Determine action type
                let action: 'add' | 'remove' | 'update';
                let changedField: 'key' | 'value' | 'both' | 'row' | undefined;

                if (afterMetadata.length > capturedBeforeMetadata.length) {
                  action = 'add';
                  changedField = 'row';
                } else if (afterMetadata.length < capturedBeforeMetadata.length) {
                  action = 'remove';
                  changedField = 'row';
                } else {
                  action = 'update';
                  // Detect which field changed
                  for (let i = 0; i < capturedBeforeMetadata.length; i++) {
                    const before = capturedBeforeMetadata[i] as any;
                    const after = afterMetadata[i] as any;
                    if (!isEqual(before, after)) {
                      const keyChanged = before?.key !== after?.key;
                      const valueChanged = before?.value !== after?.value;
                      if (keyChanged && valueChanged) {
                        changedField = 'both';
                      } else if (keyChanged) {
                        changedField = 'key';
                      } else if (valueChanged) {
                        changedField = 'value';
                      }
                      break;
                    }
                  }
                }

                const propertyCommand = new SmapPropertyCommand(
                  this.mapService,
                  {
                    mapId,
                    action,
                    beforeMetadata: capturedBeforeMetadata,
                    afterMetadata,
                    changedField
                  }
                );
                this.undoRedoManager.pushCommand(propertyCommand);
                this.beforeMetadata = undefined; // Reset for next change
              }

              if (!hastMetadataChanged) {
                this.mapService.setCurrentSelectedMapToStore(map);
              }
              this.mapMetaChanged.emit(map);
            });
        }
      });
    this.styleForm.valueChanges
      .pipe(
        debounceTime(this.debounceTime),
        filter(() => !!this.selectedMap && this.styleForm.valid),
        map(value => {
          // Initialize styles if null/undefined
          const styles = this.selectedMap!.styles || {};
          const normalizedMapAppearance = styles.appearance ?? DefaultValues.appearance.smap;
          const hasChanged = normalizedMapAppearance !== value.appearance ||
            styles.color !== value.color ||
            styles.colorIntensity !== value.colorIntensity ||
            styles.backgroundImageUrl !== value.imageUrl ||
            styles.opacity !== value.imageOpacity ||
            (styles.backgroundImageSize ?? 0) !== value.backgroundImageSize ||
            styles.backgroundSoundUrl !== value.backgroundSoundUrl ||
            styles.backgroundSoundVolume !== value.backgroundSoundVolume;
          return {value, hasChanged};
        }),
        filter(({hasChanged}) => this.isEditable && hasChanged),
        switchMap(({value}) => {
          // Capture "before" styles for undo (only if not already captured)
          if (this.beforeStyles === undefined) {
            this.beforeStyles = {
              styles: this.selectedMap?.styles ? {...this.selectedMap.styles} : {}
            };
          }

          // Check if color changed and apply color intensity adjustment logic
          const currentStyles = this.selectedMap!.styles || {};
          const colorChanged = currentStyles.color !== value.color;
          let finalColorIntensity = value.colorIntensity;

          if (colorChanged && currentStyles.colorIntensity === 0) {
            finalColorIntensity = 0.1;
            // Update the form control to reflect the new intensity
            this.styleForm.get('colorIntensity')?.setValue(0.1, { emitEvent: false });
          }

          const newStyles = {
            appearance: value.appearance === 'default' ? null : value.appearance,
            color: value.color,
            colorIntensity: finalColorIntensity,
            backgroundImageUrl: value.imageUrl,
            opacity: value.imageOpacity,
            backgroundImageSize: value.backgroundImageSize,
            // Preserve the immutable center point set at creation; the form never edits it.
            backgroundImageCenter: currentStyles.backgroundImageCenter,
            backgroundSoundUrl: value.backgroundSoundUrl,
            backgroundSoundVolume: value.backgroundSoundVolume
          };

          this.selectedMap!.styles = newStyles;
          this.cyService.updateStylesForAppearanceChange();

          const mapId = this.selectedMap!.id;
          const capturedBeforeStyles = this.beforeStyles;

          return this.mapService.update(this.selectedMap!).pipe(
            tap(() => {
              // Merge new styles with existing map instead of replacing with server response
              const currentMap = this.mapService.getCurrentSelectedMapFromStore();
              if (currentMap && currentMap.id === mapId) {
                currentMap.styles = {...newStyles};
                this.mapService.setCurrentSelectedMapToStore({...currentMap});
              }
              this.mapAudioIntegrationService.handleMapStyleUpdate(this.selectedMap!);

              // Create and push SmapStyleCommand for undo
              if (capturedBeforeStyles && mapId) {
                // Detect which property actually changed for the label
                const beforeInner = (capturedBeforeStyles as any).styles || {};
                const changedStyles: Record<string, unknown> = {};
                if (beforeInner.appearance !== newStyles.appearance) changedStyles['appearance'] = newStyles.appearance;
                if (beforeInner.color !== newStyles.color) changedStyles['color'] = newStyles.color;
                if (beforeInner.colorIntensity !== newStyles.colorIntensity) changedStyles['colorIntensity'] = newStyles.colorIntensity;
                if (beforeInner.backgroundImageUrl !== newStyles.backgroundImageUrl) changedStyles['backgroundImageUrl'] = newStyles.backgroundImageUrl;
                if (beforeInner.opacity !== newStyles.opacity) changedStyles['opacity'] = newStyles.opacity;
                if ((beforeInner.backgroundImageSize ?? 0) !== newStyles.backgroundImageSize) changedStyles['backgroundImageSize'] = newStyles.backgroundImageSize;
                if (beforeInner.backgroundSoundUrl !== newStyles.backgroundSoundUrl) changedStyles['backgroundSoundUrl'] = newStyles.backgroundSoundUrl;
                if (beforeInner.backgroundSoundVolume !== newStyles.backgroundSoundVolume) changedStyles['backgroundSoundVolume'] = newStyles.backgroundSoundVolume;

                const styleCommand = new SmapStyleCommand(
                  this.mapService,
                  {
                    mapId,
                    beforeStyles: capturedBeforeStyles,
                    afterStyles: {styles: {...newStyles}, _changedStyles: changedStyles}
                  }
                );
                this.undoRedoManager.pushCommand(styleCommand);
                this.beforeStyles = undefined; // Reset for next change
              }
            })
          );
        })
      )
      .subscribe();

    this.mapService.mapNameChangedSubject.subscribe((name) => {
      // Set flag to prevent server requests during local updates
      this.isReceivingLocalUpdate = true;

      this.selectedMap = this.mapService.getCurrentSelectedMapFromStore();
      this.nameDescriptionFields.title = name;
      this.nameDescriptionFields.description = this.selectedMap?.description || '';

      this.mapForm.setValue({
        metadata: orderBy(this.selectedMap.metadata, 'order', ['asc'])
      }, {emitEvent: false});

      // Clear flag after a short delay to allow UI to update
      setTimeout(() => {
        this.isReceivingLocalUpdate = false;
      }, 100);
    });
    this.updateSectionCount();

    this.layoutFormControl.valueChanges
      .pipe(
        skip(1),
        filter(() => !!this.selectedMap && this.isEditable)
      )
      .subscribe((v) => {
        const newLayout = v as LayoutType;
        const mapId = this.selectedMap!.id;
        const beforeLayout = this.selectedMap!.automaticLayout || 'preset';

        // Skip if layout hasn't actually changed
        if (beforeLayout === newLayout) return;

        // Update local state
        this.selectedMap!.automaticLayout = newLayout;
        this.cyService.setLayoutChangedSignal(newLayout);

        // Save to backend
        this.mapService.update(this.selectedMap!).subscribe(() => {
          // Update store
          const currentMap = this.mapService.getCurrentSelectedMapFromStore();
          if (currentMap && currentMap.id === mapId) {
            currentMap.automaticLayout = newLayout;
            this.mapService.setCurrentSelectedMapToStore({...currentMap});
          }

          // Create undo command with cyService for layout changes
          const styleCommand = new SmapStyleCommand(
            this.mapService,
            {
              mapId,
              beforeStyles: {automaticLayout: beforeLayout},
              afterStyles: {automaticLayout: newLayout, _changedStyles: {automaticLayout: newLayout}}
            },
            this.cyService
          );
          this.undoRedoManager.pushCommand(styleCommand);
        });
    });
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
}
